/**
 * 售價矩陣預覽 — Price-level × quantity-tier preview matrix (2026-08-28).
 *
 * READ-ONLY DERIVED OUTPUT of the one engine: rows are price levels that
 * already resolve to a unit price, columns are the quantity tiers implied by
 * the product's effective brackets, and each cell is
 * `applyQuantityDiscount(levelUnitPrice, bracketDiscountPct)` — the SAME pure
 * function the resolver's quantity stage calls. No stored cells, no editable
 * cells (FABLE C2-applicability §4: a stored/editable matrix would smuggle
 * NetSuite's price-level object into a model that deliberately lacks it and
 * create a second store of truth behind the pure resolver).
 *
 * Semantics carried into the caption by the UI (mandatory, C2 §4 UX-#4 —
 * the identical grid means different order totals otherwise):
 * ALL-UNITS (blanket: the winning bracket's % applies to every unit),
 * scope PER LINE (`by_line_quantity`, the only cited basis). Marginal rates
 * are NOT implemented — the marginal-vs-all-units choice is bundled into D20
 * (Sherl) with the bracket cap and the uniformity question.
 */
import type { PriceLevel, QuantityBracket } from '@/types/api/pricing';
import { applyQuantityDiscount, roundHalfUp2, selectQuantityBracket } from './quantityPricing';

/** Research cap (Amazon 5 tiers; choice-paralysis guidance) — overflow scrolls. */
export const QUANTITY_MATRIX_MAX_TIERS = 6;

export interface QuantityMatrixCell {
  qty: number;
  /** Final unit price at this quantity, 2dp — never a %-only cell. */
  unitPrice: number;
  /**
   * Discount vs THIS ROW's qty-1 price (0 = none). The referent is named in
   * the UI: 標準售價 is 3–4 referents in SenditSell, so an unnamed badge
   * would be misinformation (C2 §4 UX-#2).
   */
  discountPct: number;
}

export interface QuantityMatrixRow {
  level: PriceLevel;
  /** Unit price at qty 1 — the row's own referent for every cell badge. */
  basePrice: number;
  cells: QuantityMatrixCell[];
}

export interface QuantityMatrixView {
  /** Column headers, ascending; always starts at 1. */
  qtyTiers: number[];
  rows: QuantityMatrixRow[];
  /** Empty ⇒ the product has no quantity pricing; the UI shows one flat column. */
  brackets: QuantityBracket[];
  currency: string;
}

export interface QuantityMatrixLevelInput {
  level: PriceLevel;
  /** Resolved unit price for the level; null rows are dropped (no rule). */
  unitPrice: number | null;
}

/**
 * Column tiers: qty 1 plus each bracket threshold, ascending, de-duplicated,
 * capped at `QUANTITY_MATRIX_MAX_TIERS`. A bracket at qty 1 collapses into
 * the first column rather than producing a duplicate.
 */
export function quantityMatrixTiers(brackets: QuantityBracket[]): number[] {
  const tiers = new Set<number>([1]);
  for (const bracket of brackets) {
    if (bracket.minQuantity > 0) tiers.add(bracket.minQuantity);
  }
  return [...tiers].sort((a, b) => a - b).slice(0, QUANTITY_MATRIX_MAX_TIERS);
}

export function buildQuantityMatrix(
  levels: QuantityMatrixLevelInput[],
  brackets: QuantityBracket[],
  currency: string
): QuantityMatrixView {
  const qtyTiers = quantityMatrixTiers(brackets);
  const rows: QuantityMatrixRow[] = [];

  for (const entry of levels) {
    if (entry.unitPrice === null) continue; // no rule for this band — not a row
    const basePrice = entry.unitPrice;
    const cells = qtyTiers.map((qty): QuantityMatrixCell => {
      const bracket = selectQuantityBracket(brackets, qty);
      const unitPrice =
        bracket === null ? basePrice : applyQuantityDiscount(basePrice, bracket.discountPct);
      // Derived from the two prices rather than echoing the bracket %, so a
      // rounded cell can never advertise a discount it did not deliver.
      const discountPct =
        basePrice === 0 ? 0 : roundHalfUp2(((basePrice - unitPrice) / basePrice) * 100);
      return { qty, unitPrice, discountPct };
    });
    rows.push({ level: entry.level, basePrice, cells });
  }

  return { qtyTiers, rows, brackets, currency };
}
